import { BaseFlow } from '~/models/base-flow.model';
import { FlowConfigurationModel } from '~/models/flow-configuration.model';
import { TRootStore } from '~/types/app.types';
import {
    IFixedFlowResponse,
    IFlowResponse,
    ITransformedFlowResponse,
    ITransformedPageResponse,
    IVariableResponse,
    TBlockResponse
} from '~/types/server.types';
import { trackError } from '~/services/logger/error.utils';
import {
    convertDateTimeToCurrentTimezone,
    shortTimezoneToFullTimezone,
    TFullTimezone
} from '~/utils/timezone.utils';
import {
    TOneHomeMeeting,
    TOneHomeMeetingResponse,
    TOneHomeMeetingsResponse
} from '~/apps/main/OneHome/one-home-types';
import dayjs from '~/common/dayjs';

export function arrayToHashMapById<T>(
    arr: T[] = [],
    idFieldName: keyof T = '_id' as keyof T,
    transformIdField: ((id: string) => string) | null = null
) {
    const result: { [key: string]: T } = {};
    arr.forEach(element => {
        const id = transformIdField
            ? transformIdField(String(element[idFieldName]))
            : String(element[idFieldName]);
        result[id] = element;
    });
    return result;
}

export enum ServerErrorType {
    FlowLoadedError = 'flow_loaded_error',
    PaymentUpdateError = 'payment_update_error',
    MAPError = 'map_error',
    UndoMAPError = 'undo_map_error',
    RefundedError = 'refunded_error'
}

export function assignBlockParentId(
    blocks: TBlockResponse[]
): Record<string, TBlockResponse> {
    const blocksHash = arrayToHashMapById(blocks, 'block_id');

    blocks.forEach(block => {
        if (!('children' in block)) {
            return;
        }

        // Validate all children point to existing blocks + update the parent_id field
        const blocksByValidity = block.children.reduce(
            (acc, childLayout) => {
                const childBlock = blocksHash[childLayout.block_id];
                if (childBlock) {
                    childBlock.parent_id = block.block_id;
                    acc.valid.push(childLayout);
                } else {
                    acc.invalid.push(childLayout);
                }

                return acc;
            },
            {
                valid: [],
                invalid: []
            }
        );

        // remove invalid children
        block.children = blocksByValidity.valid;

        if (blocksByValidity.invalid.length) {
            trackError({
                error: new Error('Block has non existing children'),
                context: {
                    flow_id: block.flow_id,
                    parent_id: block.block_id,
                    invalid_children: JSON.stringify(blocksByValidity.invalid)
                }
            });
        }
    });

    return blocksHash;
}

export function transformVariable(
    variable: IVariableResponse,
    timezone: string
): IVariableResponse {
    if (!variable.value) {
        return variable;
    }

    // we want to reformat date question variables to be in the current timezone
    // this function converts them to the MM/DD/YYYY format
    const isDateQuestionVariable = !!variable.value.start_date;
    if (isDateQuestionVariable) {
        const currentTMZDate = convertDateTimeToCurrentTimezone(
            variable.value,
            timezone
        );

        return {
            ...variable,
            value: currentTMZDate,
            raw_value: { ...currentTMZDate }
        };
    }

    // we want to reformat arrays of strings that comes in wrong string format of simple string array.
    if (variable.value[0] === '[') {
        try {
            const parsedVariableValue = JSON.parse(variable.value);
            if (!Array.isArray(parsedVariableValue)) {
                return variable;
            }
            const joinedValue = parsedVariableValue.join(', ');

            return {
                ...variable,
                value: joinedValue,
                raw_value: joinedValue
            };
        } catch {
            return variable;
        }
    }

    return variable;
}

export function transformFlowResponse(
    response: IFixedFlowResponse,
    timezone: string = null
): ITransformedFlowResponse {
    const { pages, blocks, variables, pages_info, ...flowWithoutAssociations } =
        response;

    const transformVariables = variables.map(x =>
        transformVariable(x, timezone)
    );

    // TODO: should this be moved to the page model constructor,
    // the need came from me wanting to get rid of IFlow in favour of just using Flow
    const flow = {
        ...flowWithoutAssociations,
        variables: transformVariables,
        pageIds: pages.map(page => page.page_id)
    };

    const flowBaseData = {
        flow_id: flow._id,
        flow_revision_id: flow.revision_id
    };

    const pagesInfoHash = arrayToHashMapById(pages_info, 'page_id');
    const transformedPages: ITransformedPageResponse[] = pages.map(page => ({
        ...page,
        ...flowBaseData,
        completed: pagesInfoHash[page.page_id]?.is_submitted,
        submissions: pagesInfoHash[page.page_id]?.submissions || [],
        submitted_on: pagesInfoHash[page.page_id]?.submitted_on,
        views: pagesInfoHash[page.page_id]?.views || []
    }));

    // TODO: Combine with
    const transformedBlocks: TBlockResponse[] = blocks.map(block => ({
        ...block,
        ...flowBaseData
    }));

    return {
        flow,
        pages: arrayToHashMapById(transformedPages, 'page_id'),
        blocks: assignBlockParentId(transformedBlocks),
        variables: arrayToHashMapById(transformVariables, 'variable_id')
    };
}

export function flowConfigurationToJson(
    flowConfiguration: FlowConfigurationModel
) {
    return {
        default_email_template_id: flowConfiguration.default_email_template_id,
        is_redirect_url_on: flowConfiguration.is_redirect_url_on,
        assigned_members: flowConfiguration.assigned_members,
        redirect_url: flowConfiguration.redirect_url,
        auto_expiration: flowConfiguration.auto_expiration,
        require_authentication: flowConfiguration.require_authentication,
        create_draft_from_services_selection:
            flowConfiguration.create_draft_from_services_selection,
        flow_template_for_draft: flowConfiguration.flow_template_for_draft,
        cta_button_text: flowConfiguration.cta_button_text,
        is_logo_enabled: flowConfiguration.is_logo_enabled
    };
}

export function flowToJson<RootStore extends TRootStore>(
    flow: BaseFlow<RootStore>
): IFlowResponse {
    return {
        _id: flow._id,
        canceled: flow.canceled,
        changes_pending_approval: flow.changes_pending_approval,
        company_id: flow.company_id,
        configuration: flowConfigurationToJson(flow.configuration),
        currency: flow.currency,
        expired: flow.expired,
        first_published_at: flow.first_published_at,
        is_published: flow.is_published,
        is_template: flow.is_template,
        members: flow.members,
        project_id: flow.project_id,
        status: flow.status,
        summary: flow.summary,
        title: flow.title,
        workspace_id: flow.workspace_id,
        actions: [],
        blocks: [],
        variables: [],
        created_at: null,
        pages: [],
        pages_info: null,
        last_action_number: null,
        submitted_on: null,
        flow_tags: [],
        theme: flow._theme,
        card_on_file_enabled: true,
        published_at: null,
        action_block_types: [],
        last_changed_at: null
    };
}

export const oneHomeTransformMeeting = (
    meeting: TOneHomeMeetingResponse,
    companyTimezone: TFullTimezone | null
): TOneHomeMeeting => {
    const defaultBrowserTimezone =
        Intl.DateTimeFormat().resolvedOptions().timeZone;

    let startDate: Date;
    let endDate: Date;

    // type project has a different date format
    // also all_day slots has a different date format
    // so we need to convert it to the same format as the other types
    // TODO: remove this once we have a proper fix for the timezone issue in the backend
    if (meeting.all_day) {
        // 2024-01-07T00:00:00.000+00:00 -> 2024-01-07
        startDate = dayjs(meeting.start_date_and_time.split('T')[0]).toDate();
        endDate = dayjs(meeting.end_date_and_time).toDate();
    } else if (meeting.type === 'project') {
        // 2024-01-07T03:00:00.000-07:00 -> 2024-01-07T03:00:00
        const timezoneToApply =
            shortTimezoneToFullTimezone[meeting.timezone] ||
            companyTimezone ||
            defaultBrowserTimezone;

        startDate = dayjs
            .tz(meeting.start_date_and_time?.split('.')[0], timezoneToApply)
            .toDate();

        endDate = dayjs
            .tz(meeting.end_date_and_time.split('.')[0], timezoneToApply)
            .toDate();
    } else {
        startDate = dayjs(meeting.start_date_and_time).toDate();
        endDate = dayjs(meeting.end_date_and_time).toDate();
    }

    return {
        ...meeting,
        start_date_and_time: startDate,
        end_date_and_time: endDate
    };
};

export function transformMeetingsResponse(
    response: TOneHomeMeetingsResponse,
    companyTimezone: TFullTimezone | null
): TOneHomeMeeting[] {
    return response
        .map(meeting => oneHomeTransformMeeting(meeting, companyTimezone))
        .sort(
            (a, b) =>
                a.start_date_and_time.getTime() -
                b.start_date_and_time.getTime()
        );
}
